// ─── La registrazione dall'assistente — il contratto del ponte ──────────────
//
// 🗣️ 28/09/2026, mockup «Registrazione dall'assistente» approvato (*«mockup
// approvato, scelte consigliate»*): chi scrive all'assistente e NON è nel
// gestionale si registra da solo. Il numero arriva dal bottone di Telegram,
// verificato; nome e cognome li scrive lui; al ✅ nasce la scheda, attiva.
//
// ⭐ La scheda la crea il GESTIONALE, che ricontrolla il numero nello stesso
// istante: il bot riferisce l'esito, non lo decide.
// 🔒 Scelta 2A: un numero che sta su una scheda ARCHIVIATA non fa nascere una
// seconda scheda — la riattiva la Segreteria.
import { z } from 'zod';
import { identitaSchema } from './ponte.js';
/** Fin dove arriva un nome o un cognome scritto dal socio. */
export const LUNGHEZZA_NOME_REGISTRAZIONE = 40;
/** Lettere, spazi, apostrofi e trattini; comincia con una lettera. */
const FORMA_DEL_NOME = /^\p{L}[\p{L}' ’-]*$/u;
/**
 * Il nome com'è scritto, con gli spazi rimessi in ordine e le maiuscole messe
 * **solo** se è tutto minuscolo o tutto maiuscolo: «mario de luca» →
 * «Mario De Luca», «MARIO» → «Mario». Scritto con le maiuscole, resta com'è
 * («McEnroe», «de Rosa» se lo vuole così).
 *
 * ⚠️ La stessa regola sta nel bot (`registrazione.ts`), che la usa per il
 * riepilogo: le due copie hanno le stesse prove, perché il socio deve vedere
 * nel riepilogo esattamente quello che finisce nella scheda.
 */
export function nomeSistemato(scritto) {
    const s = scritto.trim().replace(/\s+/g, ' ');
    if (s !== s.toLowerCase() && s !== s.toUpperCase())
        return s;
    return s
        .toLowerCase()
        .replace(/(^|[\s'’-])(\p{L})/gu, (_, prima, lettera) => prima + lettera.toUpperCase());
}
function parteDelNome(cosa) {
    const regola = `${cosa} può avere solo lettere, spazi, apostrofi e trattini, fino a ${LUNGHEZZA_NOME_REGISTRAZIONE} caratteri: scrivimelo di nuovo.`;
    return z
        .string()
        .transform(nomeSistemato)
        .pipe(z.string().min(1, regola).max(LUNGHEZZA_NOME_REGISTRAZIONE, regola).regex(FORMA_DEL_NOME, regola));
}
export const nomeRegistrazioneSchema = parteDelNome('Il nome');
export const cognomeRegistrazioneSchema = parteDelNome('Il cognome');
/**
 * Il corpo di `POST /consumer/players/register`.
 *
 * ⚠️ La rotta legge nome e cognome A MANO (come «📝 I miei dati»): un nome che
 * non va bene torna `rifiutato` con la frase per il socio, non come un `400`
 * che il bot racconterebbe come guasto.
 */
export const domandaRegistrazioneSchema = z.object({
    /** Il numero condiviso col bottone di Telegram: il bot lo manda com'è arrivato. */
    phone: z.string().trim().min(3).max(40),
    firstName: z.string().max(200),
    lastName: z.string().max(200),
});
/**
 * Perché no, quando il verdetto è `rifiutato`:
 * - `nome` · `cognome` — la forma non va: la frase dice come scriverlo;
 * - `telefono` — il numero non si legge come numero;
 * - `da_rinnovare` — il numero è di una scheda archiviata (scelta 2A);
 * - `due_schede` — il numero è già su due schede vive: lo sistema la Segreteria.
 */
export const MOTIVI_REGISTRAZIONE = ['nome', 'cognome', 'telefono', 'da_rinnovare', 'due_schede'];
export const esitoRegistrazioneSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato']),
    reason: z.string().nullable(),
    code: z.enum(MOTIVI_REGISTRAZIONE).nullable(),
    /**
     * `true` = la scheda è nata adesso. `false` con `fatto` = col numero c'era
     * già una scheda viva (la Segreteria l'ha creata nel frattempo): il bot
     * entra con quella, e non ne nasce una seconda.
     */
    created: z.boolean(),
    player: identitaSchema.nullable(),
});
/** Da dove viene una scheda (`members.source`). */
export const ORIGINE_ASSISTENTE = 'assistente';
//# sourceMappingURL=registrazione.js.map